import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, FileText, CheckSquare, Play, Loader2, ClipboardList, GitBranch, AlertTriangle, Sparkles } from 'lucide-react';
import { RequirementsTab } from '../components/RequirementsTab';
import { TestCasesTab } from '../components/TestCasesTab';
import { TestRunsTab } from '../components/TestRunsTab';
import { TestPlanTab } from '../components/TestPlanTab';
import { TraceabilityTab } from '../components/TraceabilityTab';
import { ReleasePackTab } from '../components/ReleasePackTab';

const UsageItem = ({ label, used, limit }) => {
  const pct = Math.min((used / limit) * 100, 100);
  const nearLimit = pct >= 80;
  const atLimit = used >= limit;
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className={`font-mono font-medium ${atLimit ? 'text-red-600' : nearLimit ? 'text-amber-600' : 'text-slate-600'}`}>
          {used} / {limit}
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${atLimit ? 'bg-red-500' : nearLimit ? 'bg-amber-500' : 'bg-emerald-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const UsageBar = ({ usage }) => {
  const { requirements: r, scenarios: s, testcases: t } = usage;
  const anyNear = [r, s, t].some(x => x.used / x.limit >= 0.8);
  const anyAt = [r, s, t].some(x => x.used >= x.limit);

  return (
    <Card className={`p-4 mb-6 ${anyAt ? 'border-red-200 bg-red-50/50' : anyNear ? 'border-amber-200 bg-amber-50/30' : ''}`} data-testid="usage-bar">
      <div className="flex items-center gap-6">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
          {usage.plan} plan
        </span>
        <UsageItem label="Requirements" used={r.used} limit={r.limit} />
        <UsageItem label="Scenarios" used={s.used} limit={s.limit} />
        <UsageItem label="Test Cases" used={t.used} limit={t.limit} />
      </div>
      {anyAt && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-red-200" data-testid="limit-warning">
          <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-600">
            You've reached your plan limit. <span className="font-semibold">Upgrade to Pro</span> for higher limits and priority AI processing.
          </p>
        </div>
      )}
      {!anyAt && anyNear && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-amber-200" data-testid="near-limit-warning">
          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
          <p className="text-xs text-amber-600">You're nearing your plan limit. Consider upgrading for more capacity.</p>
        </div>
      )}
    </Card>
  );
};

export const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['requirements', 'testplan', 'testcases', 'traceability', 'testruns', 'releasepack'];
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'requirements';
  const [project, setProject] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [testRuns, setTestRuns] = useState([]);
  const [scenarioCount, setScenarioCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [testPlanRefreshKey, setTestPlanRefreshKey] = useState(0);
  const [usage, setUsage] = useState(null);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  useEffect(() => {
    loadProject();
    loadRequirements();
    loadTestCases();
    loadTestRuns();
    loadScenarioCount();
    loadUsage();
  }, [projectId]);

  const loadProject = async () => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const loadRequirements = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/requirements`);
      setRequirements(response.data);
    } catch (error) {
      console.error('Failed to load requirements');
    }
  };

  const loadTestCases = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/testcases`);
      setTestCases(response.data);
    } catch (error) {
      console.error('Failed to load test cases');
    }
  };

  const loadTestRuns = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/testruns`);
      setTestRuns(response.data);
    } catch (error) {
      console.error('Failed to load test runs');
    }
  };

  const loadScenarioCount = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/scenarios`);
      setScenarioCount((response.data || []).length);
    } catch { /* ignore */ }
  };

  const loadUsage = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/usage`);
      setUsage(response.data);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-azure" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Project not found</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="gap-2" data-testid="back-to-dashboard-button">
            <ArrowLeft className="h-4 w-4" />Back to Dashboard
          </Button>
          <button onClick={() => navigate('/pricing')} className="text-sm text-slate-500 hover:text-blue-600 font-medium transition-colors" data-testid="nav-pricing-link">
            Pricing
          </button>
        </div>

        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-heading font-bold text-blueprint tracking-tight mb-2">{project.name}</h1>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className="px-2 py-1 bg-muted rounded-full font-medium">{project.platform}</span>
                {project.domain && <span>{project.domain}</span>}
              </div>
            </div>
          </div>
          {project.modules && project.modules.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {project.modules.map((module) => (
                <span key={module} className="px-3 py-1 bg-accent text-accent-foreground rounded-md text-sm font-medium">{module}</span>
              ))}
            </div>
          )}
          {(requirements.length > 0 || scenarioCount > 0 || testCases.length > 0) && (
            <p className="text-sm text-muted-foreground mt-3" data-testid="pipeline-summary">
              <span className="font-medium">{requirements.length}</span> Requirements
              <span className="mx-1.5 text-muted-foreground/50">&rarr;</span>
              <span className="font-medium">{scenarioCount}</span> Scenarios
              <span className="mx-1.5 text-muted-foreground/50">&rarr;</span>
              <span className="font-medium">{testCases.length}</span> Test Cases
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Requirements</p>
                <p className="text-3xl font-mono font-bold text-blueprint">{requirements.length}</p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Test Cases</p>
                <p className="text-3xl font-mono font-bold text-blueprint">{testCases.length}</p>
              </div>
              <CheckSquare className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Test Runs</p>
                <p className="text-3xl font-mono font-bold text-blueprint">{testRuns.length}</p>
              </div>
              <Play className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
        </div>

        {/* Usage limits bar */}
        {usage && <UsageBar usage={usage} />}

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6">
            <TabsTrigger value="requirements" data-testid="requirements-tab">Requirements</TabsTrigger>
            <TabsTrigger value="testplan" data-testid="testplan-tab">
              <ClipboardList className="h-4 w-4 mr-1.5" />Test Plan
            </TabsTrigger>
            <TabsTrigger value="testcases" data-testid="testcases-tab">Test Cases</TabsTrigger>
            <TabsTrigger value="traceability" data-testid="traceability-tab">
              <GitBranch className="h-4 w-4 mr-1.5" />Traceability
            </TabsTrigger>
            <TabsTrigger value="testruns" data-testid="testruns-tab">Test Runs</TabsTrigger>
            <TabsTrigger value="releasepack" data-testid="releasepack-tab">
              <Sparkles className="h-4 w-4 mr-1.5" />Release Pack
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requirements">
            <RequirementsTab projectId={projectId} project={project} requirements={requirements} onUpdate={() => { loadRequirements(); loadScenarioCount(); loadUsage(); }} usage={usage} />
          </TabsContent>

          <TabsContent value="testplan">
            <TestPlanTab projectId={projectId} onTestPlanUpdate={testPlanRefreshKey} />
          </TabsContent>

          <TabsContent value="testcases">
            <TestCasesTab 
              projectId={projectId} 
              testCases={testCases} 
              requirements={requirements}
              testRuns={testRuns}
              onUpdate={() => {
                loadTestCases();
                loadScenarioCount();
                setTestPlanRefreshKey(prev => prev + 1);
                loadUsage();
              }}
              usage={usage}
            />
          </TabsContent>

          <TabsContent value="testruns">
            <TestRunsTab projectId={projectId} testRuns={testRuns} testCases={testCases} onUpdate={loadTestRuns} />
          </TabsContent>

          <TabsContent value="traceability">
            <TraceabilityTab projectId={projectId} onUpdate={() => {
              loadTestCases();
              loadScenarioCount();
              setTestPlanRefreshKey(prev => prev + 1);
              loadUsage();
            }} />
          </TabsContent>

          <TabsContent value="releasepack">
            <ReleasePackTab projectId={projectId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
